import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "../../redux/hooks";

interface Props {
  id: string;
  tag?: string;
  setValue: (id: string, value: any) => void;
}

const useFWCalculation = ({ id, tag, setValue }: Props) => {
  const { getValues, watch } = useFormContext();
  const { lbentry, features } = useSelector(state => state.generatorSlice);

  const findFieldByTag = useCallback(
    (targetTag: string) => {
      const currentValues = getValues();
      const sections = lbentry?.sections || [];

      for (const section of sections) {
        if (section.rows) {
          for (const row of section.rows) {
            if (row.cols) {
              for (const [fieldId, col] of Object.entries(row.cols)) {
                if ((col as any).tag === targetTag) {
                  return { key: fieldId, value: currentValues[fieldId] };
                }
              }
            }
          }
        }

        if (section.sections) {
          for (const nestedSection of section.sections) {
            if (nestedSection.rows) {
              for (const row of nestedSection.rows) {
                if (Array.isArray(row)) {
                  for (const col of row) {
                    if ((col as any).tag === targetTag) {
                      for (const [fieldId, value] of Object.entries(
                        currentValues,
                      )) {
                        if (value === (col as any).value) {
                          return { key: fieldId, value };
                        }
                      }
                    }
                  }
                } else if ((row as any).cols) {
                  for (const [fieldId, col] of Object.entries(
                    (row as any).cols,
                  )) {
                    if ((col as any).tag === targetTag) {
                      return { key: fieldId, value: currentValues[fieldId] };
                    }
                  }
                }
              }
            }
          }
        }
      }

      return null;
    },
    [getValues, lbentry?.sections],
  );

  const calculateFW = useCallback(() => {
    if (!tag || !["ss-prod", "ss-cons"].includes(tag)) {
      return;
    }

    let ssTreated =
      (lbentry as any)?.ssTreated ||
      features?.ssTreated ||
      (window as any)?.ssTreated;

    if (!ssTreated || typeof ssTreated !== "object") {
      return;
    }

    const primaryTreatedField = findFieldByTag("ss-pri-treated");
    const secondaryTreatedField = findFieldByTag("ss-sec-treated");

    let currentValue = 0;
    let previousValue = 0;
    let calculatedValue = 0;

    if (tag === "ss-prod") {
      if (!primaryTreatedField || !primaryTreatedField.value) {
        return;
      }
      currentValue = parseFloat(primaryTreatedField.value) || 0;
      previousValue = parseFloat(ssTreated["ss-pri-treated"] || 0);
      calculatedValue = currentValue - previousValue;
    } else if (tag === "ss-cons") {
      if (!secondaryTreatedField || !secondaryTreatedField.value) {
        return;
      }
      currentValue = parseFloat(secondaryTreatedField.value) || 0;
      previousValue = parseFloat(ssTreated["ss-sec-treated"] || 0);
      calculatedValue = currentValue - previousValue;
    }

    if (currentValue === 0) {
      return;
    }

    if (Math.abs(calculatedValue) < 1000000) {
      setValue(id, calculatedValue.toFixed(2));
    }
  }, [id, tag, lbentry, features, setValue, findFieldByTag]);

  useEffect(() => {
    if (tag && ["ss-prod", "ss-cons"].includes(tag)) {
      const timer = setTimeout(() => {
        calculateFW();
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [tag, calculateFW]);

  useEffect(() => {
    if (tag && ["ss-prod", "ss-cons"].includes(tag)) {
      const primaryTreatedField = findFieldByTag("ss-pri-treated");
      const secondaryTreatedField = findFieldByTag("ss-sec-treated");

      const subscription = watch((values, { name }) => {
        let shouldRecalculate = false;

        if (
          tag === "ss-prod" &&
          primaryTreatedField &&
          name === primaryTreatedField.key
        ) {
          shouldRecalculate = true;
        } else if (
          tag === "ss-cons" &&
          secondaryTreatedField &&
          name === secondaryTreatedField.key
        ) {
          shouldRecalculate = true;
        }

        if (shouldRecalculate) {
          const timer = setTimeout(() => {
            calculateFW();
          }, 150);
          return () => clearTimeout(timer);
        }
      });

      return subscription.unsubscribe;
    }
  }, [tag, watch, calculateFW, findFieldByTag]);

  return calculateFW;
};

export default useFWCalculation;
